import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ApiTaskKanbanStatus } from "src/api/types";
import Link from "src/components/Link";
import Loader from "src/components/Loader";
import Kanban from "src/components/kanban/Kanban";
import TaskStatus from "src/components/tasks/TaskStatus";
import TaskKanbanCard from "src/components/tasks/kanban/TaskKanbanCard";
import useTasksFilter from "src/hooks/api/tasks/useTasksFilter";
import useTasksKanban from "src/hooks/api/tasks/useTasksKanban";
import { useUpdateEffectDebounce } from "src/hooks/useDebounce";
import useNavigateModal from "src/hooks/useNavigateModal";

interface TasksKanbanColumnProps {
	status: ApiTaskKanbanStatus;
}

const TasksKanbanColumn = ({ status }: TasksKanbanColumnProps) => {
	const { state, actions } = useTasksKanban();
	const navigateModal = useNavigateModal();
	const navigate = useNavigate();
	const column = state.columns[status];
	const { filter } = useTasksFilter();
	useUpdateEffectDebounce(filter, 1000, () => {
		actions.getColumn(
			status,
			{
				page: 1,
			},
			"reset"
		);
	});

	useEffect(() => {
		actions.getColumn(status);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Kanban.Column
			id={status}
			key={status}
			head={
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<TaskStatus size="regular" {...{ status }} />
						{column.status === "loading" && <Loader />}
					</div>
					<Link
						className="w-8 h-8 flex justify-center items-center hover:bg-dark-100 rounded-md"
						to={`/tasks?modal=create-intern-task&status=${status}`}
					>
						<i className="far fa-plus text-[16px]"></i>
					</Link>
				</div>
			}
		>
			{column.tasks.map((task) => (
				<Kanban.Item
					options={{
						disabled: task.type !== "INTERNAL",
					}}
					column={status}
					id={task.id}
					key={`${status}-${task.id}`}
				>
					<TaskKanbanCard
						{...{ task }}
						onClick={(e) => {
							e.stopPropagation();
							if (task.type === "INTERNAL") {
								navigateModal(`/tasks/${task.hid}/show`);
							} else {
								navigate(`/tasks/${task.hid}/show`);
							}
						}}
					/>
				</Kanban.Item>
			))}

			{column?.meta &&
				column.meta.current_page < column.meta.last_page && (
					<Kanban.LoadMore
						onClick={() =>
							actions.getColumn(status, {
								page: column.meta.current_page + 1,
							})
						}
						loading={column.status === "loading"}
					/>
				)}
		</Kanban.Column>
	);
};

export default TasksKanbanColumn;
